/**
 * Входной контроль партий (КЧ-02, СК-06): партии поступлений, которые ждут решения ОТК, и уже решённые, —
 * с пробами и протоколом. Решение принимается в документе поступления (кнопки «Принять» / «Брак»).
 */
import type { IncomingControlRow } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { qty } from './queries';

const STATES = { pending: 'Ждёт решения ОТК', accepted: 'Принято', rejected: 'Брак' } as const;
const TONE = { pending: 'warning', accepted: 'success', rejected: 'danger' } as const;

export function IncomingControlTab() {
  const [state, setState] = useState<'pending' | 'accepted' | 'rejected'>('pending');
  const q = useQuery({
    queryKey: ['wh', 'incoming-control', state],
    queryFn: () => api<IncomingControlRow[]>(`/warehouse/incoming-control?state=${state}`),
  });
  return (
    <Panel
      title="Входной контроль партий"
      actions={
        <Select
          className="h-11 w-auto"
          aria-label="Состояние"
          value={state}
          onChange={(e) => setState(e.target.value as typeof state)}
        >
          {Object.entries(STATES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.length ? (
        <ul className="divide-y divide-line">
          {q.data.map((r) => (
            <li key={r.lineId} className="flex flex-col gap-1 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={TONE[r.qcStatus]}>{STATES[r.qcStatus]}</Badge>
                <Link
                  to="/warehouse/items/$itemId"
                  params={{ itemId: r.itemId }}
                  className="inline-flex min-h-11 items-center font-medium hover:underline"
                >
                  {r.itemName}
                </Link>
                <span className="num text-[13px] text-ink-2">
                  {qty(r.quantity)} {r.unit}
                </span>
              </div>
              <div className="text-[13px] text-ink-2">
                <Link
                  to="/warehouse/documents/$docId"
                  params={{ docId: r.documentId }}
                  className="inline-flex min-h-11 items-center hover:underline"
                >
                  {r.documentNumber} от {date(r.docDate)}
                </Link>{' '}
                · {r.warehouseName}
                {r.projectCode ? ` · ${r.projectCode}` : ''}
                {r.batch ? ` · партия ${r.batch}` : ''}
                {r.certNumber ? ` · сертификат ${r.certNumber}` : ''}
                {r.passportNumber ? ` · паспорт ${r.passportNumber}` : ''}
                {r.qcStatus === 'pending' && r.waitingDays > 0 ? ` · ждёт ${r.waitingDays} дн.` : ''}
              </div>
              {r.sampleQty || r.protocolNo ? (
                <div className="text-[13px] text-ink-2">
                  {r.sampleQty
                    ? `Проб: ${qty(r.sampleQty)} ${r.unit}${r.sampleNote ? ` (${r.sampleNote})` : ''}`
                    : ''}
                  {r.protocolNo
                    ? `${r.sampleQty ? ' · ' : ''}протокол ${r.protocolNo}${r.protocolDate ? ` от ${date(r.protocolDate)}` : ''}`
                    : ''}
                </div>
              ) : null}
              {r.qcNote ? <div className="text-[13px] text-ink-2">{r.qcNote}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Нет партий">
          Позиции с требованием сертификата или паспорта попадают сюда после поступления
        </EmptyState>
      )}
    </Panel>
  );
}
